import { useMemo, useState } from "react";
import * as React from "react";
import { useSnackbar } from "notistack";
import { ApplicationState } from "../../store/index";
import { SiteSyncingState } from "../../store/Sites";
import { useNavigate, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { useSiteBasePath } from "../../shared/utils/siteNavPath";

import {
  apiClientFactory,
  defaultSiteTimeZone,
  getComprehensiveTimeZones,
  isNorthAmericaTimeZoneForDisplay,
  isKnownIanaTimeZone,
  preferredSiteTimeZones,
  type TimeZoneOption,
} from "../../helpers/index";
import { SiteSystemType } from "api/ApiClient";
import { TimeZoneCombobox } from "components/shared/TimeZoneCombobox";
import { Button } from "components/ui/button";

interface RouteParams extends Record<string, string | undefined> {
  siteId: string;
}

const SiteEditComponent: React.FC = () => {
  const navigate = useNavigate();
  const basePath = useSiteBasePath();
  const { siteId } = useParams<RouteParams>();
  const sites = useSelector((state: ApplicationState) => state.sites);
  const { enqueueSnackbar } = useSnackbar();

  const site = sites.sites.find((e: any) => e.id === siteId);

  const [localSite, setLocalSite] = useState<SiteSyncingState>(
    site ?? {
      id: "",
      propertyExternalId: "",
      name: "",
      timeZone: "",
      username: "",
      password: "",
      sharedKey: "",
      sharedKeyName: "",
    },
  );

  const [customTimeZone, setCustomTimeZone] = useState("");
  const [timeZoneSearchText, setTimeZoneSearchText] = useState("");

  const tenantUsedTimeZones = useMemo(
    () =>
      Array.from(
        new Set(
          (sites.sites || [])
            .map((siteItem) => siteItem.timeZone)
            .filter((timeZone): timeZone is string => !!timeZone?.trim()),
        ),
      ),
    [sites.sites],
  );

  const timeZoneOptions = useMemo<TimeZoneOption[]>(
    () => {
      const options = getComprehensiveTimeZones(tenantUsedTimeZones)
        .filter(isNorthAmericaTimeZoneForDisplay)
        .map((timeZone) => ({
          value: timeZone,
          isPreferred: preferredSiteTimeZones.includes(timeZone),
          isInUse: tenantUsedTimeZones.includes(timeZone),
        }));

      const normalizedSelectedTimeZone = localSite.timeZone?.trim() || "";
      const hasSelectedTimeZoneInOptions = options.some(
        (option) => option.value === normalizedSelectedTimeZone,
      );

      if (normalizedSelectedTimeZone && !hasSelectedTimeZoneInOptions) {
        options.unshift({
          value: normalizedSelectedTimeZone,
          isPreferred: preferredSiteTimeZones.includes(normalizedSelectedTimeZone),
          isInUse: tenantUsedTimeZones.includes(normalizedSelectedTimeZone),
        });
      }

      return options;
    },
    [localSite.timeZone, tenantUsedTimeZones],
  );

  const selectedTimeZoneOption = useMemo(
    () =>
      timeZoneOptions.find((option) => option.value === localSite.timeZone) ||
      null,
    [localSite.timeZone, timeZoneOptions],
  );

  const effectiveTimeZone = (customTimeZone || localSite.timeZone || "").trim();
  const resolvedTimeZone = effectiveTimeZone || defaultSiteTimeZone;
  const hasNoSelectedTimeZone = !effectiveTimeZone;
  const hasUnknownCustomTimeZone =
    !!customTimeZone.trim() && !isKnownIanaTimeZone(customTimeZone.trim());

  const filterTimeZoneOptions = (
    options: TimeZoneOption[],
    inputValue: string,
  ) => {
    const normalizedInput = inputValue.trim().toLowerCase();

    if (!normalizedInput) {
      return options.slice(0, 200);
    }

    const filtered = options.filter((option) =>
      option.value.toLowerCase().includes(normalizedInput),
    );

    const hasExactMatch = options.some(
      (option) => option.value.toLowerCase() === normalizedInput,
    );

    if (!hasExactMatch) {
      filtered.push({
        value: inputValue.trim(),
        isPreferred: false,
        isInUse: false,
        isCustomAction: true,
      });
    }

    return filtered.slice(0, 200);
  };

  const submit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    let payload: any = {
      timeZone: resolvedTimeZone,
      name: localSite.name,
      propertyExternalId: localSite.propertyExternalId,
    };
    if (localSite?.accessCode) {
      payload.accessCode = localSite.accessCode;
    }
    if (localSite?.proxyVersion) {
      payload.proxyVersion = localSite.proxyVersion;
    }

    if (localSite.proxyVersion === 2) {
      payload.password = localSite.password || "";
      payload.username = localSite.username || "";
      payload.sharedKey = localSite.sharedKey || "";
      payload.sharedKeyName = localSite.sharedKeyName || "";
    }
    try {
      if (!siteId) {
        enqueueSnackbar(
          <span style={{ fontSize: 12 }}>Site ID is required</span>,
          { variant: "error" },
        );
        return;
      }
      await apiClientFactory().updateSite(siteId, payload);
      enqueueSnackbar(
        <span style={{ fontSize: 12 }}>Site updated successfully</span>,
        { variant: "success" },
      );
      navigate(`${basePath}/`);
    } catch (e: any) {
      const msg = e?.message || "Error updating site";
      enqueueSnackbar(<span style={{ fontSize: 12 }}>{msg}</span>, {
        variant: "error",
      });
    }
  };

  return (
    <main className="container">
      <div className="bg-white p-4">
        <form
          className="form"
          onSubmit={(e) => submit(e)}
          style={{ marginTop: "20px" }}
        >
          <div className="form-group">
            <label htmlFor="propertyExternalId">Property External Id</label>
            <input
              type="text"
              id="propertyExternalId"
              className="form-control"
              required
              value={localSite?.propertyExternalId}
              onChange={(e) =>
                setLocalSite({
                  ...localSite,
                  propertyExternalId: e.target.value,
                })
              }
            />
          </div>
          <div className="form-group">
            <label htmlFor="siteName">Site Name</label>
            <input
              type="text"
              id="siteName"
              className="form-control"
              required
              value={localSite?.name}
              onChange={(e) =>
                setLocalSite({ ...localSite, name: e.target.value })
              }
            />
          </div>
          <div className="form-group">
            <label htmlFor="timeZone">Time Zone</label>
            <TimeZoneCombobox
              ariaLabel="Time Zone"
              filterOptions={filterTimeZoneOptions}
              id="timeZone"
              inputValue={timeZoneSearchText}
              onClear={() => {
                setCustomTimeZone("");
                setLocalSite({ ...localSite, timeZone: "" });
                setTimeZoneSearchText("");
              }}
              onInputValueChange={setTimeZoneSearchText}
              onSelectOption={(option) => {
                if (option.isCustomAction) {
                  setCustomTimeZone(option.value.trim());
                  setLocalSite({ ...localSite, timeZone: "" });
                  setTimeZoneSearchText(option.value.trim());
                  return;
                }

                setCustomTimeZone("");
                setLocalSite({ ...localSite, timeZone: option.value });
                setTimeZoneSearchText(option.value);
              }}
              options={timeZoneOptions}
              placeholder="Search timezone"
              value={selectedTimeZoneOption}
            />
            {hasUnknownCustomTimeZone && (
              <small className="form-text text-warning">
                Not known as IANA timezone. You can still save this value.
              </small>
            )}
            {hasNoSelectedTimeZone && (
              <small className="form-text text-warning">
                No timezone selected. (Default: {defaultSiteTimeZone})
              </small>
            )}
          </div>
          {localSite.account &&
            localSite.account.systemType === SiteSystemType.SchlageEngage && (
              <div className="form-group">
                <label htmlFor="accessCode">Access code </label>
                <input
                  className="form-control"
                  name="accessCode"
                  placeholder="Access code"
                  maxLength={12}
                  value={localSite.accessCode}
                  onChange={(e) =>
                    setLocalSite({ ...localSite, accessCode: e.target.value })
                  }
                />
              </div>
            )}
          {localSite.account &&
            [
              SiteSystemType.Assa,
              SiteSystemType.SaltoSpace,
              SiteSystemType.DormaKaba,
            ].includes(localSite.account.systemType!) && (
              <>
                <div className="form-group">
                  <label htmlFor="proxyVersion">Proxy Version </label>
                  <input
                    className="form-control"
                    name="proxyVersion"
                    placeholder=">Proxy Version"
                    type="number"
                    min="1"
                    max="2"
                    step="1"
                    value={localSite.proxyVersion}
                    onChange={(e) => {
                      const inputValue = parseFloat(e.target.value);
                      if (
                        !isNaN(inputValue) &&
                        inputValue >= 1 &&
                        inputValue <= 2
                      ) {
                        setLocalSite({
                          ...localSite,
                          proxyVersion: inputValue,
                        });
                      }
                    }}
                  />
                </div>
                {localSite.proxyVersion === 2 && (
                  <>
                    <div className="form-group">
                      <label htmlFor="password">Update Proxy Username</label>
                      <input
                        className="form-control"
                        name="username"
                        placeholder="Proxy Username"
                        value={localSite.username}
                        onChange={(e) => {
                          setLocalSite({
                            ...localSite,
                            username: e.target.value,
                          });
                        }}
                      />
                    </div>
                    <div className="form-group">
                      <label htmlFor="password">Update Proxy Password</label>
                      <input
                        className="form-control"
                        name="password"
                        placeholder="Proxy Password"
                        value={localSite.password}
                        onChange={(e) => {
                          setLocalSite({
                            ...localSite,
                            password: e.target.value,
                          });
                        }}
                      />
                    </div>
                    <div className="form-group">
                      <label htmlFor="sharedKey">Shared Key</label>
                      <input
                        className="form-control"
                        name="sharedKey"
                        placeholder="Shared Key"
                        value={localSite.sharedKey}
                        onChange={(e) => {
                          setLocalSite({
                            ...localSite,
                            sharedKey: e.target.value,
                          });
                        }}
                      />
                    </div>
                    <div className="form-group">
                      <label htmlFor="sharedKeyName">Shared Key Name</label>
                      <input
                        className="form-control"
                        name="sharedKeyName"
                        placeholder="Shared Key Name"
                        value={localSite.sharedKeyName}
                        onChange={(e) => {
                          setLocalSite({
                            ...localSite,
                            sharedKeyName: e.target.value,
                          });
                        }}
                      />
                    </div>
                  </>
                )}
              </>
            )}

          <div className="form-group">
            <Button
              className="w-full"
              type="submit"
              disabled={
                !site ||
                !localSite.name ||
                !localSite.propertyExternalId
              }
            >
              Update
            </Button>
          </div>
        </form>
      </div>
    </main>
  );
};

export const SiteEdit = SiteEditComponent;

export default SiteEdit;
